import type {
  EmployerFootprintAdminViewDto,
  EmployerFootprintCompanyProfileDto,
} from '../api/employerVerificationDashboardApi';
import type { TabNarrative, TabNarrativeTone } from './applicationTabNarratives';
import { buildEmploymentUnderReviewNarrative } from './employment/employmentUnderReviewCopy';

function confidencePct(value: number | null | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return `${Math.round(value * 100)}%`;
}

function trimOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const t = value.trim();
  return t || null;
}

function normalizeEmployerNameForDisplay(name: string): string {
  return name.replace(/[.\s]+$/, '').trim();
}

export type EmployerFootprintPlainStory = {
  headline: string;
  /** Single-line fallback for tab narratives and legacy callers. */
  detail: string | null;
  /** Short bullets for the footprint alert panel. */
  detailPoints: string[];
  tone: TabNarrativeTone;
};

function footprintStoryDetail(points: string[]): string | null {
  return points.length > 0 ? points.join(' ') : null;
}

/** Prefer persisted operator copy; fall back to bare reason code. */
export function footprintOperatorReason(
  view: Pick<
    EmployerFootprintAdminViewDto,
    'adminMessage' | 'paystubsRequiredReason' | 'statusReason'
  >,
): string | null {
  return (
    trimOrNull(view.adminMessage) ||
    trimOrNull(view.paystubsRequiredReason) ||
    trimOrNull(view.statusReason)
  );
}

/**
 * Display purchase-area cluster. Clarifies misleading AI "no debit data" when
 * Income already has debits (or this analysis simply lacked usable POS spend).
 */
export function formatFootprintPurchaseArea(
  cluster: string | null | undefined,
  opts?: { purchasesAnalyzed?: number | null },
): string | null {
  const trimmed = trimOrNull(cluster);
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (!/\bno debit data\b/.test(lower) && !/\bno debit descriptions?\b/.test(lower)) {
    return trimmed;
  }
  if ((opts?.purchasesAnalyzed ?? 0) > 0) return trimmed;
  if (/no debit descriptions in this analysis/i.test(trimmed)) return trimmed;
  if (/no usable purchase locations/i.test(trimmed)) return trimmed;
  return 'Unknown (no usable purchase locations in bank debits)';
}

function footprintPurchaseDetail(
  locationEvidence: EmployerFootprintAdminViewDto['locationEvidence'],
): string | null {
  const purchasePins = locationEvidence?.purchasesWithCoordinates?.length ?? 0;
  if (purchasePins > 0) {
    return `Mapped ${purchasePins} purchase location${purchasePins === 1 ? '' : 's'} from bank activity.`;
  }
  if (locationEvidence && locationEvidence.purchasesAnalyzed > 0) {
    return `Reviewed ${locationEvidence.purchasesAnalyzed} purchase description${
      locationEvidence.purchasesAnalyzed === 1 ? '' : 's'
    } from bank activity.`;
  }
  return null;
}

function footprintClusterDetail(cluster: string | null): string | null {
  if (!cluster) return null;
  return `Spending cluster: ${cluster.replace(/[.\s]+$/, '')}.`;
}

export function footprintConfidenceDetail(
  conf: string | null,
  opts: { confirmPath: boolean; hasOperatorReason: boolean },
): string | null {
  if (!conf) return null;
  if (opts.confirmPath) {
    return `About ${conf} confidence — based on spending locations and bank payor signals. This is not proof of employment.`;
  }
  if (opts.hasOperatorReason) {
    return `Confidence about ${conf}.`;
  }
  return `Confidence about ${conf} — needs a closer look or paystubs.`;
}

/** Plain-English story for salary digital footprint (before formal employer verification). */
export function buildEmployerFootprintPlainStory(
  view: EmployerFootprintAdminViewDto,
): EmployerFootprintPlainStory {
  const status = String(view.status ?? '').toUpperCase();
  const rawName = view.detectedEmployer?.trim() || null;
  const name = rawName ? normalizeEmployerNameForDisplay(rawName) : null;
  const conf = confidencePct(view.overallConfidence);
  const cluster = formatFootprintPurchaseArea(
    view.locationEvidence?.geographicCluster,
    { purchasesAnalyzed: view.locationEvidence?.purchasesAnalyzed },
  );
  const operatorReason = footprintOperatorReason(view);
  const purchaseBit = footprintPurchaseDetail(view.locationEvidence);
  const clusterBit = footprintClusterDetail(cluster);

  if (status === 'PENDING' || status === 'RUNNING') {
    const detailPoints = [
      'This check runs before formal employer verification.',
      'Results appear here when the analysis finishes.',
    ];
    return {
      headline: 'Looking at bank spending to find the employer.',
      detailPoints,
      detail: footprintStoryDetail(detailPoints),
      tone: 'info',
    };
  }

  if (status === 'FAILED') {
    const detailPoints = [
      operatorReason || 'The customer may need to upload paystubs instead.',
    ];
    return {
      headline: 'Digital footprint check could not finish.',
      detailPoints,
      detail: footprintStoryDetail(detailPoints),
      tone: 'error',
    };
  }

  if (status === 'READY') {
    if (view.journeyHint === 'CONFIRM' && name) {
      const detailPoints = [
        footprintConfidenceDetail(conf, { confirmPath: true, hasOperatorReason: false }),
        'Needs human review before treating this as the confirmed employer.',
        purchaseBit,
        clusterBit,
      ].filter((point): point is string => Boolean(point));
      return {
        headline: `Possible employer from bank activity: ${name}`,
        detailPoints,
        detail: footprintStoryDetail(detailPoints),
        tone: 'warning',
      };
    }
    if (name) {
      const detailPoints = [
        operatorReason,
        footprintConfidenceDetail(conf, {
          confirmPath: false,
          hasOperatorReason: Boolean(operatorReason),
        }),
        !operatorReason && !conf ? 'Needs a closer look or paystubs.' : null,
        purchaseBit,
        clusterBit,
      ].filter((point): point is string => Boolean(point));
      return {
        headline: `Possible employer from bank activity: ${name}`,
        detailPoints,
        detail: footprintStoryDetail(detailPoints),
        tone: 'warning',
      };
    }
    const detailPoints = [
      operatorReason,
      purchaseBit,
      !operatorReason ? 'Paystubs may be needed next.' : null,
    ].filter((point): point is string => Boolean(point));
    return {
      headline: 'Digital footprint finished without a clear employer.',
      detailPoints,
      detail: footprintStoryDetail(detailPoints),
      tone: 'warning',
    };
  }

  return {
    headline: 'Digital footprint has not started yet.',
    detailPoints: [],
    detail: null,
    tone: 'info',
  };
}

/** True when salary/debit footprint analysis has been kicked off (income path). */
export type FootprintFactRow = {
  label: string;
  value: string;
  href?: string;
};

export function formatFootprintEnumLabel(value: string | null | undefined): string | null {
  const trimmed = trimOrNull(value);
  if (!trimmed) return null;
  return trimmed
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Year-only when the model used YYYY-01-01 as a year placeholder. */
export function formatFootprintIncorporationDate(value: string | null | undefined): string | null {
  const trimmed = trimOrNull(value);
  if (!trimmed) return null;
  const yearOnly = /^(\d{4})-01-01$/.exec(trimmed);
  if (yearOnly) return yearOnly[1];
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!iso) return trimmed;
  const date = new Date(`${iso[1]}-${iso[2]}-${iso[3]}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return trimmed;
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

function formatScorePct(value: number | null | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return `${Math.round(value * 100)}%`;
}

function yesNo(value: boolean | null | undefined): string | null {
  if (typeof value !== 'boolean') return null;
  return value ? 'Yes' : 'No';
}

function pushFact(
  rows: FootprintFactRow[],
  label: string,
  value: string | null | undefined,
  href?: string | null,
): void {
  const text = trimOrNull(value);
  if (!text) return;
  rows.push(href ? { label, value: text, href } : { label, value: text });
}

export function buildFootprintEntityFactRows(
  profile: EmployerFootprintCompanyProfileDto | null | undefined,
): FootprintFactRow[] {
  const entity = profile?.entityProfile;
  if (!entity) return [];
  const rows: FootprintFactRow[] = [];
  pushFact(rows, 'Incorporated', formatFootprintIncorporationDate(entity.incorporationDate));
  pushFact(rows, 'Jurisdiction', entity.incorporationJurisdiction);
  pushFact(rows, 'Entity status', formatFootprintEnumLabel(entity.entityStatus));
  pushFact(rows, 'Entity type', formatFootprintEnumLabel(entity.entityType));
  pushFact(rows, 'Organization', formatFootprintEnumLabel(entity.organizationCategory));
  pushFact(rows, 'Ownership country', entity.ownershipCountry);
  pushFact(rows, 'Ultimate parent', entity.ultimateParentName);
  pushFact(rows, 'Publicly traded', yesNo(entity.publiclyTraded));
  pushFact(rows, 'Ticker', entity.stockTickerExchange);
  const naics = trimOrNull(entity.naics);
  if (naics) {
    pushFact(rows, 'NAICS', entity.naicsInferred === true ? `${naics} (inferred)` : naics);
  }
  pushFact(rows, 'Note', entity.amalgamationNote);
  pushFact(rows, 'Registry record', entity.registryRecordId);
  const registryUrl = trimOrNull(entity.registrySourceUrl);
  pushFact(rows, 'Registry source', registryUrl, registryUrl);
  return rows;
}

export function buildFootprintPeopleFactRows(
  profile: EmployerFootprintCompanyProfileDto | null | undefined,
): FootprintFactRow[] {
  const rows: FootprintFactRow[] = [];
  const founders = (profile?.founders ?? []).map((name) => name.trim()).filter(Boolean);
  if (founders.length) pushFact(rows, 'Founders', founders.join(', '));
  pushFact(rows, 'Global CEO', profile?.executives?.globalCeo);
  pushFact(rows, 'Canada president', profile?.executives?.canadaPresident);
  pushFact(rows, 'Canada CFO', profile?.executives?.canadaCfo);
  return rows;
}

export type FootprintSignalChipState = 'found' | 'missing' | 'caution';

export type FootprintSignalChip = {
  id: string;
  label: string;
  state: FootprintSignalChipState;
};

export type FootprintDriverId = 'payor' | 'web' | 'geo';

export type FootprintDriverRow = {
  id: FootprintDriverId;
  label: string;
  score: number | null;
  chips: FootprintSignalChip[];
};

export type FootprintScoreBand = 'high' | 'mid' | 'low';

/** Display band for a 0–1 persist score. Not a gate verdict. */
export function footprintScoreBand(score: number | null | undefined): FootprintScoreBand | null {
  if (typeof score !== 'number' || !Number.isFinite(score)) return null;
  if (score >= 0.85) return 'high';
  if (score >= 0.55) return 'mid';
  return 'low';
}

export type FootprintConfidenceWord = 'model-strong' | 'needs review' | 'weak';

export function footprintConfidencePresentation(
  view: Pick<
    EmployerFootprintAdminViewDto,
    'overallConfidence' | 'meetsConfidenceThreshold' | 'status' | 'journeyHint' | 'detectedEmployer'
  >,
): { pct: number | null; word: FootprintConfidenceWord; tone: 'success' | 'warning' | 'error' } {
  const pct =
    typeof view.overallConfidence === 'number' && Number.isFinite(view.overallConfidence)
      ? Math.round(view.overallConfidence * 100)
      : null;
  const status = String(view.status ?? '').toUpperCase();
  if (status === 'FAILED' || (pct != null && pct < 50) || !trimOrNull(view.detectedEmployer)) {
    return { pct, word: 'weak', tone: 'error' };
  }
  if (view.meetsConfidenceThreshold || view.journeyHint === 'CONFIRM') {
    return { pct, word: 'model-strong', tone: 'success' };
  }
  return { pct, word: 'needs review', tone: 'warning' };
}

export function extractQuotedPayorString(reasoning: string | null | undefined): string | null {
  const text = trimOrNull(reasoning);
  if (!text) return null;
  const match = text.match(/'([^']{3,80})'|"([^"]{3,80})"/);
  const quoted = trimOrNull(match?.[1] ?? match?.[2]);
  return quoted;
}

export function buildFootprintDriverRows(args: {
  profile: EmployerFootprintCompanyProfileDto | null | undefined;
  purchaseArea: string | null;
  distanceKm: number | null;
  declaredGeocoded: boolean;
  mismatchMeters: number | null;
}): FootprintDriverRow[] {
  const { profile } = args;
  const payorChips: FootprintSignalChip[] = [];
  const quoted = extractQuotedPayorString(profile?.stringMatchReasoning);
  if (quoted) {
    payorChips.push({ id: 'raw-payor', label: `raw: ${quoted}`, state: 'found' });
  }
  if (trimOrNull(profile?.stringMatchReasoning)) {
    payorChips.push({ id: 'name-noise', label: 'name after noise', state: 'found' });
  } else {
    payorChips.push({ id: 'name-noise', label: 'name after noise', state: 'missing' });
  }

  const website = trimOrNull(profile?.officialWebsite);
  const linkedIn = trimOrNull(profile?.linkedInUrl);
  const webChips: FootprintSignalChip[] = [
    { id: 'website', label: website ? 'website listed' : 'website — not found', state: website ? 'found' : 'missing' },
    { id: 'linkedin', label: linkedIn ? 'LinkedIn listed' : 'LinkedIn — not found', state: linkedIn ? 'found' : 'missing' },
  ];

  const geoChips: FootprintSignalChip[] = [];
  if (args.purchaseArea) {
    geoChips.push({ id: 'cluster', label: `spend in ${args.purchaseArea}`, state: 'found' });
  } else {
    geoChips.push({ id: 'cluster', label: 'spend cluster — not found', state: 'missing' });
  }
  if (args.distanceKm != null && Number.isFinite(args.distanceKm)) {
    geoChips.push({
      id: 'distance',
      label: `${Math.round(args.distanceKm)} km from HQ`,
      state: 'found',
    });
  }
  if (args.declaredGeocoded) {
    geoChips.push({ id: 'geocode', label: 'declared address geocoded', state: 'found' });
  }
  if (args.mismatchMeters != null && Number.isFinite(args.mismatchMeters)) {
    geoChips.push({
      id: 'mismatch',
      label: `geocode off by ${Math.round(args.mismatchMeters)} m`,
      state: 'caution',
    });
  }

  return [
    {
      id: 'payor',
      label: 'Payor match',
      score: profile?.breakdown?.extractionConfidence ?? null,
      chips: payorChips,
    },
    {
      id: 'web',
      label: 'Web presence',
      score: profile?.breakdown?.webExistenceConfidence ?? null,
      chips: webChips,
    },
    {
      id: 'geo',
      label: 'Geography',
      score: profile?.breakdown?.locationCorrelationScore ?? null,
      chips: geoChips,
    },
  ];
}

export function footprintPeoplePreview(rows: FootprintFactRow[]): string | null {
  if (rows.length === 0) return null;
  const first = rows[0]?.value?.trim();
  if (!first) return `${rows.length} name${rows.length === 1 ? '' : 's'}`;
  const extra = rows.length - 1;
  return extra > 0 ? `${first} +${extra}` : first;
}

export type FootprintGraphCategory =
  | 'identity'
  | 'people'
  | 'digital'
  | 'geography'
  | 'payor';

export type FootprintGraphNode = {
  id: string;
  label: string;
  detail?: string;
  href?: string;
  category: FootprintGraphCategory;
  weight: number;
};

export type FootprintOsintKind =
  | 'company'
  | 'ownership'
  | 'location'
  | 'digital'
  | 'sector'
  | 'entity'
  | 'people'
  | 'leaf';

/** Advisory branch signal — not pass/fail. */
export type FootprintOsintBranchSignal = {
  type: 'address-differs';
  distanceLabel?: string | null;
};

export type FootprintOsintLeafRole =
  | 'declared-address'
  | 'ai-footprint'
  | 'address-distance';

export type FootprintOsintTreeNode = {
  id: string;
  label: string;
  value?: string;
  href?: string;
  kind?: FootprintOsintKind;
  children?: FootprintOsintTreeNode[];
  /** Optional advisory signal on factor branches (e.g. address mismatch). */
  signal?: FootprintOsintBranchSignal;
  leafRole?: FootprintOsintLeafRole;
};

function normalizeAddressCompareKey(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Detect declared vs AI footprint address discrepancy for OSINT graph emphasis. */
export function resolveFootprintAddressSignal(args: {
  declaredAddress: string | null;
  aiAddress: string | null;
  mismatchMeters?: number | null;
  distanceKm?: number | null;
}): FootprintOsintBranchSignal | null {
  const declared = trimOrNull(args.declaredAddress);
  const ai = trimOrNull(args.aiAddress);
  if (!declared || !ai) return null;

  const textDiffers = normalizeAddressCompareKey(declared) !== normalizeAddressCompareKey(ai);
  const geocodeDiffers =
    args.mismatchMeters != null &&
    Number.isFinite(args.mismatchMeters) &&
    args.mismatchMeters > 0;

  if (!textDiffers && !geocodeDiffers) return null;

  let distanceLabel: string | null = null;
  if (geocodeDiffers) {
    const km = args.mismatchMeters! / 1000;
    distanceLabel =
      km >= 1
        ? `${km.toFixed(1)} km between geocodes`
        : `${Math.round(args.mismatchMeters!)} m between geocodes`;
  } else if (args.distanceKm != null && Number.isFinite(args.distanceKm)) {
    distanceLabel = `${args.distanceKm.toFixed(1)} km from purchase cluster`;
  }

  return { type: 'address-differs', distanceLabel };
}

/** Default expanded branches when opening the graph — surface location when it differs. */
export function defaultOsintExpandedIds(tree: FootprintOsintTreeNode): string[] {
  const ids = ['company'];
  const location = tree.children?.find((child) => child.id === 'location');
  if (location?.signal?.type === 'address-differs') {
    ids.push('location');
  }
  return ids;
}

function leaf(id: string, label: string, value: string | null | undefined, href?: string | null): FootprintOsintTreeNode | null {
  const text = trimOrNull(value);
  if (!text) return null;
  return href
    ? { id, label, value: text, href, kind: 'leaf' }
    : { id, label, value: text, kind: 'leaf' };
}

function branch(
  id: FootprintOsintKind,
  label: string,
  children: Array<FootprintOsintTreeNode | null>,
  signal?: FootprintOsintBranchSignal,
): FootprintOsintTreeNode | null {
  const present = children.filter((child): child is FootprintOsintTreeNode => child != null);
  if (present.length === 0) return null;
  return { id, label, kind: id, children: present, ...(signal ? { signal } : {}) };
}

/** OSINT-style tree: main factors first; people / entity extras sit one click deeper. */
export function buildFootprintOsintTree(args: {
  companyName: string | null;
  profile: EmployerFootprintCompanyProfileDto | null | undefined;
  declaredAddress: string | null;
  aiAddress: string | null;
  purchaseArea: string | null;
  phone: string | null;
  mismatchMeters?: number | null;
  distanceKm?: number | null;
}): FootprintOsintTreeNode {
  const entity = args.profile?.entityProfile;
  const people = [
    leaf('ceo', 'Global CEO', args.profile?.executives?.globalCeo),
    leaf('president', 'Canada president', args.profile?.executives?.canadaPresident),
    leaf('cfo', 'Canada CFO', args.profile?.executives?.canadaCfo),
    leaf(
      'founders',
      'Founders',
      (args.profile?.founders ?? []).map((name) => name.trim()).filter(Boolean).join(', ') || null,
    ),
  ];

  const addressSignal = resolveFootprintAddressSignal({
    declaredAddress: args.declaredAddress,
    aiAddress: args.aiAddress,
    mismatchMeters: args.mismatchMeters,
    distanceKm: args.distanceKm,
  });

  const declaredLeaf = leaf('declared', 'Declared address', args.declaredAddress);
  const aiLeaf = leaf('ai-addr', 'AI footprint address', args.aiAddress);
  const locationChildren: Array<FootprintOsintTreeNode | null> = [
    declaredLeaf
      ? { ...declaredLeaf, leafRole: 'declared-address' as const }
      : null,
    aiLeaf ? { ...aiLeaf, leafRole: 'ai-footprint' as const } : null,
    addressSignal?.distanceLabel
      ? {
          id: 'addr-distance',
          label: 'Geocode separation',
          value: addressSignal.distanceLabel,
          kind: 'leaf',
          leafRole: 'address-distance',
        }
      : null,
    leaf('area', 'Purchase area', args.purchaseArea),
  ];

  const children = [
    branch('ownership', 'Ownership', [
      leaf('parent', 'Ultimate parent', entity?.ultimateParentName),
      leaf('org', 'Owned as', formatFootprintEnumLabel(entity?.organizationCategory)),
      leaf('country', 'Country', entity?.ownershipCountry ?? args.profile?.companyOriginCountry),
      typeof entity?.publiclyTraded === 'boolean'
        ? { id: 'traded', label: 'Publicly traded', value: entity.publiclyTraded ? 'Yes' : 'No', kind: 'leaf' }
        : null,
    ]),
    branch('location', 'Location', locationChildren, addressSignal ?? undefined),
    branch('digital', 'Website and contact', [
      leaf('website', 'Website', args.profile?.officialWebsite, args.profile?.officialWebsite),
      leaf('phone', 'Phone', args.phone),
      trimOrNull(args.profile?.linkedInUrl)
        ? leaf('linkedin', 'LinkedIn', args.profile?.linkedInUrl, args.profile?.linkedInUrl)
        : { id: 'linkedin', label: 'LinkedIn', value: 'Not found', kind: 'leaf' },
    ]),
    branch('sector', 'Sector', [
      leaf('sector', 'Sector', args.profile?.sector),
      leaf(
        'naics',
        'NAICS',
        entity?.naics
          ? entity.naicsInferred
            ? `${entity.naics} (inferred)`
            : entity.naics
          : null,
      ),
    ]),
    branch('entity', 'Entity record', [
      leaf('status', 'Entity status', formatFootprintEnumLabel(entity?.entityStatus)),
      leaf('type', 'Entity type', formatFootprintEnumLabel(entity?.entityType)),
      leaf('inc', 'Incorporated', formatFootprintIncorporationDate(entity?.incorporationDate)),
      leaf('jurisdiction', 'Jurisdiction', entity?.incorporationJurisdiction),
    ]),
    branch('people', 'People', people),
  ].filter((child): child is FootprintOsintTreeNode => child != null);

  return {
    id: 'company',
    label: args.companyName?.trim() || 'Possible employer',
    kind: 'company',
    children,
  };
}

export function buildFootprintGraphNodes(args: {
  companyName: string | null;
  entityRows: FootprintFactRow[];
  peopleRows: FootprintFactRow[];
  website: string | null;
  linkedIn: string | null;
  phone: string | null;
  declaredAddress: string | null;
  aiAddress: string | null;
  purchaseArea: string | null;
  payorReason: string | null;
}): FootprintGraphNode[] {
  const nodes: FootprintGraphNode[] = [
    {
      id: 'company',
      label: args.companyName?.trim() || 'Possible employer',
      category: 'identity',
      weight: 1,
    },
  ];
  for (const row of args.entityRows) {
    nodes.push({
      id: `entity-${row.label}`,
      label: row.label,
      detail: row.value,
      href: row.href,
      category: 'identity',
      weight: 0.7,
    });
  }
  for (const row of args.peopleRows) {
    nodes.push({
      id: `person-${row.label}`,
      label: row.value,
      detail: row.label,
      category: 'people',
      weight: 0.65,
    });
  }
  if (args.website) {
    nodes.push({ id: 'website', label: 'Website', detail: args.website, href: args.website, category: 'digital', weight: 0.8 });
  }
  if (args.linkedIn) {
    nodes.push({ id: 'linkedin', label: 'LinkedIn', detail: args.linkedIn, href: args.linkedIn, category: 'digital', weight: 0.45 });
  } else {
    nodes.push({ id: 'linkedin-missing', label: 'LinkedIn — not found', category: 'digital', weight: 0.2 });
  }
  if (args.phone) {
    nodes.push({ id: 'phone', label: 'Phone listed', detail: args.phone, category: 'digital', weight: 0.55 });
  }
  if (args.declaredAddress) {
    nodes.push({ id: 'declared', label: 'Declared address', detail: args.declaredAddress, category: 'geography', weight: 0.75 });
  }
  if (args.aiAddress) {
    nodes.push({ id: 'ai-addr', label: 'AI footprint address', detail: args.aiAddress, category: 'geography', weight: 0.5 });
  }
  if (args.purchaseArea) {
    nodes.push({ id: 'cluster', label: 'Purchase area', detail: args.purchaseArea, category: 'geography', weight: 0.8 });
  }
  if (args.payorReason) {
    nodes.push({ id: 'payor', label: 'Payor match', detail: args.payorReason, category: 'payor', weight: 0.85 });
  }
  return nodes;
}

export function buildFootprintScoreFactRows(
  profile: EmployerFootprintCompanyProfileDto | null | undefined,
): FootprintFactRow[] {
  const rows: FootprintFactRow[] = [];
  pushFact(rows, 'Payor match', formatScorePct(profile?.breakdown?.extractionConfidence));
  pushFact(rows, 'Web presence', formatScorePct(profile?.breakdown?.webExistenceConfidence));
  pushFact(rows, 'Geography', formatScorePct(profile?.breakdown?.locationCorrelationScore));
  pushFact(rows, 'Why this match', profile?.reasoningSummary);
  pushFact(rows, 'How the payor matched', profile?.stringMatchReasoning);
  const penalties = (profile?.confidencePenalties ?? [])
    .map((item) => item.trim())
    .filter(Boolean);
  if (penalties.length) pushFact(rows, 'Review notes', penalties.join(' · '));
  return rows;
}

export function isEmployerFootprintActive(status: string | null | undefined): boolean {
  const s = String(status ?? '').toUpperCase();
  return s === 'PENDING' || s === 'RUNNING' || s === 'READY' || s === 'FAILED';
}

/**
 * Map footprint status onto employer tab chrome when formal verification has not started.
 * Enables the Employer tab (pending stays locked).
 */
export function employerTabStatusFromFootprint(
  status: string | null | undefined,
): 'review' | 'fail' | null {
  const s = String(status ?? '').toUpperCase();
  if (s === 'FAILED') return 'fail';
  if (s === 'PENDING' || s === 'RUNNING' || s === 'READY') return 'review';
  return null;
}
export function mergeEmployerNarrativeWithFootprint(
  base: TabNarrative,
  footprint: EmployerFootprintAdminViewDto | null | undefined,
): TabNarrative {
  if (!footprint) return base;
  const status = String(footprint.status ?? '').toUpperCase();
  if (status === 'NOT_STARTED') return base;

  if (/is under review/i.test(base.headline)) {
    const enriched = buildEmploymentUnderReviewNarrative({ footprint });
    const hasSpecificTrigger =
      /could not be automatically verified/i.test(enriched.headline) ||
      Boolean(enriched.detail && enriched.detail !== base.detail);
    if (hasSpecificTrigger) {
      return {
        ...base,
        headline: enriched.headline,
        detail: enriched.detail ?? base.detail,
      };
    }
    return base;
  }

  const formalHasOutcome =
    /employment verified|could not be confirmed|still needs a closer look/i.test(
      base.headline,
    );
  if (formalHasOutcome) return base;

  const story = buildEmployerFootprintPlainStory(footprint);
  return {
    headline: story.headline,
    tone: story.tone,
    detail: story.detail,
    bullets: story.detailPoints,
  };
}
